import { createSlice } from "@reduxjs/toolkit";
import { api } from "service/api";
import { ReportMgmtApi, ReportsApi } from "service/apiVariables";
import { Toast } from "service/toast";
import { setLoader } from "./loader";

const today = new Date();

const past7Days = new Date();
past7Days.setDate(today.getDate() - 7);

const EMPTY_REPORTS = {
  data_list: [],
  pageMeta: {
    total: 0,
  },
};

const getIndiaDate = (date) =>
  date.toLocaleDateString("en-CA", {
    timeZone: "Asia/Kolkata",
  });

const getErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.message ||
  error?.message ||
  fallbackMessage ||
  "Something went wrong";

const showErrorToast = (message) => {
  Toast({
    type: "error",
    message,
  });
};

const showSuccessToast = (message) => {
  Toast({
    type: "success",
    message,
  });
};

const handleCommonError = (error, fallbackMessage) => {
  const errorMessage = getErrorMessage(error, fallbackMessage);
  showErrorToast(errorMessage);
  throw error;
};

const mapSavedFilterItem = (item = {}) => ({
  ...item,
  id: item?.id ?? item?.saved_filter_id ?? item?.filter_id,
  name: item?.name ?? item?.filter_name ?? "",
  filters: item?.filters ?? item?.filter_payload ?? {},
  filter_payload: item?.filter_payload ?? item?.filters ?? {},
  module_name: item?.module_name ?? item?.module ?? "",
  is_default: Number(item?.is_default ?? 0),
});

const getSavedFilterList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data_list)) return data.data_list;
  if (Array.isArray(data?.result)) return data.result;

  return [];
};

const normalizeSavedFilters = (data) => {
  const list = getSavedFilterList(data);

  return list.map(mapSavedFilterItem);
};

const normalizeSingleSavedFilter = (data, fallbackBody = null) => {
  const raw = data?.data || data?.result || data || fallbackBody;

  if (!raw) return null;

  return mapSavedFilterItem(raw);
};

const isSameModule = (item, moduleName) => {
  const itemModuleName = String(item?.module_name || "").toLowerCase();
  const currentModuleName = String(moduleName || "").toLowerCase();

  return itemModuleName === currentModuleName;
};

const filterSavedFiltersByModule = (list, moduleName) =>
  list.filter((item) => isSameModule(item, moduleName));

const handleSavedFilterError = (dispatch, error, fallbackMessage) => {
  const errorMessage = getErrorMessage(error, fallbackMessage);

  dispatch(setSavedFilterError(errorMessage));
  showErrorToast(errorMessage);

  throw error;
};

export const reportsSlice = createSlice({
  name: "reports",
  initialState: {
    isLoading: true,
    reportDetail: null,
    reports: EMPTY_REPORTS,
    selectedProject: null,
    selectedWing: null,
    selectedTrade: null,
    selectedTechnician: null,
    startDate: getIndiaDate(today),
    endDate: getIndiaDate(past7Days),
    reportLoading: false,
    sortKey: null,
    sortType: "DESC",
    savedFilters: [],
    selectedSavedFilter: null,
    loading: false,
    saveLoading: false,
    deleteLoading: false,
    error: null,
  },
  reducers: {
    saveReports: (state, action) => {
      state.reports = action.payload;
      state.isLoading = false;
    },

    saveReportDetail: (state, action) => {
      state.reportDetail = action.payload;
      state.isLoading = false;
    },

    setSelectedTrade: (state, action) => {
      state.selectedTrade = action.payload;
    },

    setSelectedProject: (state, action) => {
      state.selectedProject = action.payload;
    },

    setSelectedWing: (state, action) => {
      state.selectedWing = action.payload;
    },

    setSelectedTechnician: (state, action) => {
      state.selectedTechnician = action.payload;
    },

    setStartDate: (state, action) => {
      state.startDate = action.payload;
    },

    setEndDate: (state, action) => {
      state.endDate = action.payload;
    },

    setLoading: (state, action) => {
      state.reportLoading = action.payload;
    },

    setSortKey: (state, action) => {
      state.sortKey = action.payload;
    },

    setSortType: (state, action) => {
      state.sortType = action.payload;
    },

    saveSavedFilters: (state, action) => {
      state.savedFilters = action.payload || [];
      state.loading = false;
      state.error = null;
    },

    addSavedFilter: (state, action) => {
      const newFilter = action.payload;

      if (!newFilter) {
        state.saveLoading = false;
        state.error = null;
        return;
      }

      const existingIndex = state.savedFilters.findIndex(
        (item) => String(item?.id) === String(newFilter?.id),
      );

      if (existingIndex > -1) {
        state.savedFilters[existingIndex] = newFilter;
      } else {
        state.savedFilters.unshift(newFilter);
      }

      state.saveLoading = false;
      state.error = null;
    },

    removeSavedFilter: (state, action) => {
      const id = action.payload;

      state.savedFilters = state.savedFilters.filter(
        (item) => String(item?.id) !== String(id),
      );

      state.deleteLoading = false;
      state.error = null;
    },

    setSelectedSavedFilter: (state, action) => {
      state.selectedSavedFilter = action.payload;
    },

    setSavedFiltersLoading: (state, action) => {
      state.loading = action.payload;
    },

    setSaveFilterLoading: (state, action) => {
      state.saveLoading = action.payload;
    },

    setDeleteFilterLoading: (state, action) => {
      state.deleteLoading = action.payload;
    },

    setSavedFilterError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
      state.saveLoading = false;
      state.deleteLoading = false;
    },

    clearSavedFilterError: (state) => {
      state.error = null;
    },

    resetSavedFilterState: (state) => {
      state.savedFilters = [];
      state.selectedSavedFilter = null;
      state.loading = false;
      state.saveLoading = false;
      state.deleteLoading = false;
      state.error = null;
    },
  },
});

// get individual report details
export const getIndividualReportDetails = (id) => async (dispatch) => {
  try {
    ReportMgmtApi.getReportApi.url_id = id;

    const data = await api({
      ...ReportMgmtApi.getReportApi,
    });

    dispatch(saveReportDetail(data));

    return data;
  } catch (error) {
    return handleCommonError(error, "Failed to fetch report details");
  }
};

// update report
export const updateReportAct = (body, id) => async () => {
  try {
    ReportMgmtApi.updateReportsApi.url_id = id;

    const data = await api({
      ...ReportMgmtApi.updateReportsApi,
      body,
    });

    return data;
  } catch (error) {
    return handleCommonError(error, "Failed to update report");
  }
};

// get reports filter
export const getReportsFilterAct = (params) => async (dispatch) => {
  try {
    ReportsApi.getReportsApi.addQuery = params;

    const { data } = await api({
      ...ReportsApi.getReportsApi,
    });

    dispatch(saveReports(data || EMPTY_REPORTS));
    dispatch(setLoader(false));
    dispatch(setLoading(false));

    return data;
  } catch (error) {
    dispatch(setLoader(false));
    dispatch(setLoading(false));
    dispatch(saveReports(EMPTY_REPORTS));

    return handleCommonError(error, "Failed to fetch reports");
  }
};

// filter dropdown
export const getFilterOptions = () => async () => {
  try {
    const data = await api({
      ...ReportsApi.getFilterReportOptionApi,
    });

    return data;
  } catch (error) {
    return handleCommonError(error, "Failed to fetch filter options");
  }
};

// filter wings
export const getFilterWingsApi = (id) => async () => {
  try {
    ReportsApi.getFilterWingsOptions.query.project_id = id;

    const data = await api({
      ...ReportsApi.getFilterWingsOptions,
    });

    return data;
  } catch (error) {
    return handleCommonError(error, "Failed to fetch wings");
  }
};

// download reports
export const downloadFileAct = (body) => async () => {
  try {
    const data = await api({
      ...ReportMgmtApi.downloadFileApi,
      body,
    });

    return data;
  } catch (error) {
    return handleCommonError(error, "Failed to download report");
  }
};

// get all saved filters
export const getSavedFiltersAct =
  (moduleName = "report") =>
  async (dispatch) => {
    dispatch(setSavedFiltersLoading(true));
    dispatch(clearSavedFilterError());

    try {
      const data = await api({
        api: `/saved-filter/get-by-module?module_name=${moduleName}`,
        method: "get",
        baseURL: "user",
      });

      const normalizedList = normalizeSavedFilters(data);
      const filteredList = filterSavedFiltersByModule(
        normalizedList,
        moduleName,
      );

      dispatch(saveSavedFilters(filteredList));

      return filteredList;
    } catch (error) {
      return handleSavedFilterError(
        dispatch,
        error,
        "Failed to fetch saved filters",
      );
    }
  };

// get single saved filter by id
export const getSavedFilterByIdAct = (id) => async (dispatch) => {
  dispatch(setSavedFiltersLoading(true));
  dispatch(clearSavedFilterError());

  try {
    const data = await api({
      api: `/saved-filter/get/${id}`,
      method: "get",
      baseURL: "user",
    });

    const item = normalizeSingleSavedFilter(data);

    dispatch(setSavedFiltersLoading(false));

    return item;
  } catch (error) {
    return handleSavedFilterError(
      dispatch,
      error,
      "Failed to fetch saved filter",
    );
  }
};

// create saved filter
export const createSavedFilterAct = (body) => async (dispatch) => {
  dispatch(setSaveFilterLoading(true));
  dispatch(clearSavedFilterError());

  try {
    const data = await api({
      api: "/saved-filter/create",
      method: "post",
      baseURL: "user",
      body,
    });

    const savedItem = normalizeSingleSavedFilter(data, body);

    dispatch(addSavedFilter(savedItem));

    return savedItem;
  } catch (error) {
    return handleSavedFilterError(dispatch, error, "Failed to save filter");
  }
};

// delete saved filter
export const deleteSavedFilterAct = (id) => async (dispatch) => {
  dispatch(setDeleteFilterLoading(true));
  dispatch(clearSavedFilterError());

  try {
    const data = await api({
      api: `/saved-filter/delete/${id}`,
      method: "delete",
      baseURL: "user",
    });

    dispatch(removeSavedFilter(id));

    return data;
  } catch (error) {
    return handleSavedFilterError(dispatch, error, "Failed to delete filter");
  }
};

export const {
  saveReportDetail,
  saveReports,
  setSelectedProject,
  setSelectedTrade,
  setSelectedWing,
  setSelectedTechnician,
  setStartDate,
  setEndDate,
  setLoading,
  setSortKey,
  setSortType,
  saveSavedFilters,
  addSavedFilter,
  removeSavedFilter,
  setSelectedSavedFilter,
  setSavedFiltersLoading,
  setSaveFilterLoading,
  setDeleteFilterLoading,
  setSavedFilterError,
  clearSavedFilterError,
  resetSavedFilterState,
} = reportsSlice.actions;

export default reportsSlice.reducer;